import { getStationsDataFromZone } from 'context/ZoneContext/utils';
import { Region, Station, Zone } from 'pages/Map/types';
import { MapSearchResult, PathSearchItem } from 'pages/MapSearch/types';
import { ZoneResult, ZoneWiseResult } from './types';
import { BigJunctureStation } from 'pages/Map/MyMap/Map/components/ZoneMap/components/Station';
import { PATH_ID_SEPARATOR } from 'constants/map';

/**
 * getStationsDataFromZone only returns stations and sub stations inside subzone
 * adding wrapper to extract substations inside big juncture as well for search
 */
export const getFlatStationsForSearchResult = (zone: Zone) => {
    const stationsInZone = getStationsDataFromZone(zone);
    return stationsInZone.reduce((prev: Station[], station) => {
        if (station instanceof BigJunctureStation) {
            return [...prev, station, ...station.getStations()];
        }
        return [...prev, station];
    }, []);
};

export const isPathStartingFromStation = (id: string, stationId: string) => {
    const pathIdParts = id.split(PATH_ID_SEPARATOR);
    return pathIdParts.length >= 2 && pathIdParts[0] === stationId;
};

export const getPathsStartingFromStation = (stationId: string, allZones: ZoneResult) => {
    const stationPaths: ZoneResult = {};
    for (const id in allZones) {
        if (isPathStartingFromStation(id, stationId)) {
            stationPaths[id] = allZones[id];
        }
    }
    return stationPaths;
};

export const hasPathsStartingFromStation = (stationId: string, zoneResult: ZoneResult) => {
    return zoneResult && Object.keys(zoneResult).some(id => isPathStartingFromStation(id, stationId));
};

const getZoneSearchResult = (zone: Zone, allZones: ZoneResult) => {
    const stationsInZone = getFlatStationsForSearchResult(zone);
    const searchIds = Object.keys(allZones);
    return stationsInZone
        .filter(station => searchIds.some(id => id === station.id || isPathStartingFromStation(id, station.id)))
        .reduce((prev, station) => {
            const stationResults: ZoneResult = {};
            if (allZones[station.id]) {
                stationResults[station.id] = allZones[station.id];
            }

            const pathsStartingFromStation = getPathsStartingFromStation(station.id, allZones);
            //adding path search results to start station to render along with it, otherwise all path search results will come at the end
            if (Object.keys(pathsStartingFromStation).length) {
                stationResults[station.id] = {
                    ...stationResults[station.id],
                    paths: getPathsStartingFromStation(station.id, allZones)
                };
            }

            return {
                ...prev,
                ...stationResults,
                ...pathsStartingFromStation
            };
        }, {});
};

export const groupSearchResultByStationID = (stations: MapSearchResult['stations']) => {
    return stations.reduce((prev, station) => {
        return {
            ...prev,
            [station.id]: station
        };
    }, {});
};

export const groupSearchResultByPathID = (paths: MapSearchResult['paths']): { [key: string]: PathSearchItem } => {
    return paths.reduce((prev, path) => {
        const pathId = `${path.start.id}${PATH_ID_SEPARATOR}${path.end.id}`;
        return {
            ...prev,
            [pathId]: {
                id: pathId,
                ...path
            }
        };
    }, {});
};

export const groupSearchResultByZone = (region: Region, allZones: ZoneResult) => {
    return region.zones.reduce((prevValue, zone) => {
        return {
            ...prevValue,
            [zone.name]: getZoneSearchResult(zone, allZones)
        };
    }, {});
};

export const getDataWithIdAsKey = (result: MapSearchResult) => {
    return {
        ...groupSearchResultByStationID(result.stations),
        ...groupSearchResultByPathID(result.paths)
    };
};

export const getResultCount = (zoneWiseResult: ZoneWiseResult, zone: string) => {
    const zoneResult = zoneWiseResult[zone];
    let count = 0;
    for (const id in zoneResult) {
        if (zoneResult[id].words) {
            count++;
        }
    }
    return count;
};
